import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { getUserId, getUserName } from '@redux/selectors/UserSelector';
import { getImpersonationStatus } from '@redux/selectors/ImpersonationSelector';

import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import paginationFactory, { PaginationProvider } from 'react-bootstrap-table2-paginator';
import filterFactory, { textFilter } from 'react-bootstrap-table2-filter';
import { isEmpty, isNull } from 'lodash';

import AgentAvatar from '@components/table/avatar/AgentAvatar';
import TableLoaderSmall from '@components/loaders/TableLoaderSmall';
import ListingTypeIcons from '@components/table/icons/ListingTypeIcons';
import ListingActionButtons from '@components/table/actionButtons/ListingActionButtons';
import TableSearchInputGroup from '@components/smartAdminUI/input/TableSearchInputGroup';
import StatusBadges from '@components/badges/StatusBadges';

const ListingsTableWidget = ({
  displayData,
  onListingSelect,
}) => {
  const [slicedTableData, setSlicedTableData] = useState(displayData);
  const realUserName = useSelector(getUserName);
  const realUserId = useSelector(getUserId);
  const impersonationStatus = useSelector(getImpersonationStatus);
  const listingsLoading = useSelector(state => state.listing.editableListingLoading);
  const showBrokerListings = useSelector(state => state.listing.showBrokerListings);
  const hideAgentFullName = useSelector(state => !state.listing.showBrokerListings);
  const currentUser = useMemo(() => (impersonationStatus?.ImpersonationActive
    ? { userName: impersonationStatus.memberFullName, userId: impersonationStatus.memberUniqueID }
    : { userName: realUserName, userId: realUserId }
  ), [realUserName, realUserId, impersonationStatus]);
  const actionsFormatter = (cell, row) => (
    <ListingActionButtons
      viewClicked={onListingSelect}
      copyClicked={() => {}}
      auditClicked={() => {}}
      currRow={row}
      isCopiable={false}
      isEditable={row?.isEditable}
      isAuditable={false}
      sizeSM
    />
  );

  const hdrSortClasses = (_column, sortOrder) => `sorting_${sortOrder}`;

  const filterByAgentKeys = (filterVal, data) => {
    const filteredData = data.filter(listing => {
      const listAgentKey = isNull(listing.ListAgentKeyNumeric) ? '' : listing.ListAgentKeyNumeric.toString();
      const coListAgentKey = isNull(listing.CoListAgentKeyNumeric) ? '' : listing.CoListAgentKeyNumeric.toString();
      return isNull(filterVal) || filterVal === ''
        ? true
        : listAgentKey.includes(filterVal) || coListAgentKey.includes(filterVal);
    });
    setSlicedTableData(filteredData.slice(0, 500));

    return filteredData;
  };

  useEffect(() => {
    if (isNull(currentUser.userId)) return;
    const userId = isNull(currentUser.userId) ? '' : currentUser.userId.toString();
    filterByAgentKeys(showBrokerListings ? null : userId, displayData);
    // TODO: Although it seems to work as expected, this should be revisited as it feels very hacky
    const btn = document.getElementById('hideAgentFullNameButton');
    btn.click();
  }, [currentUser, showBrokerListings, displayData]);

  const columns = useMemo(() => [
    {
      dataField: 'actions',
      text: ' ',
      formatter: actionsFormatter,
      headerStyle: { width: '30px' },
    },
    {
      dataField: 'UniqueID',
      hidden: true,
    },
    {
      dataField: 'ListingId',
      text: 'ID',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '40px' },
    },
    {
      dataField: 'StandardStatusVerb',
      text: 'Status',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      align: 'center',
      headerStyle: { width: '50px' },
      formatter: cellContent => (isEmpty(cellContent) ? '' : <StatusBadges status={cellContent} />),
    },
    {
      dataField: 'PropertyType',
      text: 'Type',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      align: 'center',
      headerStyle: { width: '40px' },
      formatter: cellContent => (isEmpty(cellContent) ? '' : <ListingTypeIcons type={cellContent} />),
    },
    {
      dataField: 'UnparsedAddress',
      text: 'Address',
      title: true,
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      classes: 'text-ellipsis-175',
    },
    {
      dataField: 'City',
      text: 'City',
      title: true,
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      classes: 'text-ellipsis-100',
    },
    {
      dataField: 'ListAgentFullName',
      text: 'Agent',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      hidden: hideAgentFullName,
      sort: true,
      rows: '1',
      align: 'center',
      headerStyle: { width: '40px' },
      formatter: cellContent => (isEmpty(cellContent) ? '' : <AgentAvatar fullName={cellContent} showFullName={false} />),
    },
    {
      dataField: 'ListAgentKeyNumeric',
      text: 'keys',
      style: { display: 'none' },
      className: 'd-none',
      headerStyle: { display: 'none' },
      sort: false,
      searchable: false,
      filter: textFilter({
        onFilter: filterByAgentKeys,
      }),
    },
    {
      dataField: 'ModifiedDate',
      text: 'ModifiedDate',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      hidden: true,
      searchable: true,
    },
  ], [hideAgentFullName]);

  // Sets the default sort order to desc by ListingId
  const defaultSorted = [{
    dataField: 'ListingId',
    order: 'desc',
  }];

  const paginationOptions = {
    sizePerPage: 5,
    pageStartIndex: 1,
    hideSizePerPage: true, // Hide the sizePerPage dropdown always
    hidePageListOnlyOnePage: true, // Hide the pagination list when only one page
    showTotal: slicedTableData?.length > 0,
  };

  const CustomToggleList = ({
    onColumnToggle,
  }) => (
    <div className="btn-group btn-group-toggle btn-group-vertical d-none" data-toggle="buttons">
      <button
        id="hideAgentFullNameButton"
        type="button"
        key="ListAgentFullName"
        data-toggle="button"
        onClick={() => onColumnToggle('ListAgentFullName')}
      >
        Agent
      </button>
    </div>
  );

  const listingsTable = ({ paginationTableProps }) => (
    <ToolkitProvider
      columns={columns}
      data={slicedTableData}
      keyField="UniqueID"
      columnToggle
      search
    >
      {toolKit => (
        <div className="dataTables_wrapper dt-bootstrap4 no-footer">
          <TableSearchInputGroup {...toolKit.searchProps} />
          <CustomToggleList {...toolKit.columnToggleProps} />
          { slicedTableData && (
            <BootstrapTable
              defaultSorted={defaultSorted}
              wrapperClasses="table-responsive"
              classes="dataTable"
              bordered={false}
              striped
              hover
              id="listingTable"
              noDataIndication="No Listing Data."
              filter={filterFactory()}
              {...toolKit.baseProps}
              {...paginationTableProps}
            />
          )}
        </div>
      )}
    </ToolkitProvider>
  );

  return (
    <>
      <div className={listingsLoading ? '' : 'd-none'}>
        <TableLoaderSmall />
      </div>
      <div className={listingsLoading ? 'd-none' : ''}>
        <PaginationProvider pagination={paginationFactory(paginationOptions)}>
          { listingsTable }
        </PaginationProvider>
      </div>
    </>
  );
};

ListingsTableWidget.propTypes = {
  displayData: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onListingSelect: PropTypes.func.isRequired,
  onColumnToggle: PropTypes.func,
  isModal: PropTypes.bool,
};

export default ListingsTableWidget;
